/**
 * ENG-4902 / ENG-4465: peer-agent message classifier for the Telegram channel
 * adapter.
 *
 * Pure functions only — no MCP, no network, no global state. The pollLoop
 * calls `classifyPeerMessage` after the existing /restart and /help
 * intercepts and uses the result to decide whether to ingress (human or
 * peer-agent), drop with telemetry, or ignore (own-message echo).
 *
 * Implements:
 *
 * - §6.3.1 fail-fast governance: peer_agent_mode → peer_group_ids → CHARTER
 *   peer registry (keyed on the immutable `from.id`). Each layer emits a
 *   distinct drop reason.
 * - §4 addressing matrix: a peer message ingresses only when at least one
 *   of (a) `bot_command` entity targeting our @username, (b) reply to one
 *   of our messages, (c) `mention` entity for our @username — substrings
 *   without an entity do not count.
 *
 * The §5 / §5.5 safety rails (rate limiter, dead-man switch, kill switch)
 * are deliberately out of scope here; they layer on top of this
 * classifier's output in MVP #4.
 */

export type PeerAgentMode = 'off' | 'listen' | 'respond';

/** Subset of the Telegram Bot API `Message` object the classifier reads. */
export interface PeerClassifierMessage {
  message_id: number;
  text?: string;
  caption?: string;
  from?: {
    id: number;
    username?: string;
    is_bot?: boolean;
  };
  chat: {
    id: number;
    type: string;
  };
  reply_to_message?: {
    message_id: number;
    from?: { id: number; is_bot?: boolean };
  };
  entities?: ReadonlyArray<{ type: string; offset: number; length: number }>;
  caption_entities?: ReadonlyArray<{ type: string; offset: number; length: number }>;
}

/**
 * ENG-4935 / ENG-4929 §4.0: gate-path metadata threaded from the manager.
 * `null` (or omitted) means "no live gate authorises this peer" — the
 * classifier drops the message with `cross_team_grant_missing`.
 *
 *  - `same_team`               — peer is on this agent's team.
 *  - `intra_org_unrestricted`  — peer is on a sibling team in the same
 *                                org and the org-level toggle is
 *                                `unrestricted`.
 *  - `grant:<grant_id>`        — peer is on a different team, authorised
 *                                by a live row in cross_team_peer_grants.
 */
export type PeerGatePath =
  | 'same_team'
  | 'intra_org_unrestricted'
  | `grant:${string}`
  | null;

export interface PeerInfo {
  code_name: string;
  bot_id: number;
  agent_id: string;
  /**
   * Optional gate path. Undefined = pre-ENG-4935 manager that didn't
   * resolve gates; treat as `same_team` (admit) to preserve old behaviour.
   * Null = explicit "gate missing" → classifier drops.
   */
  gate_path?: PeerGatePath;
}

/**
 * Per-agent peer-collaboration configuration. Built at startup from env
 * vars the manager passes in (`TELEGRAM_PEER_AGENT_MODE`,
 * `TELEGRAM_PEER_GROUP_IDS`, `TELEGRAM_PEERS`). Default `mode: 'off'`
 * preserves the single-agent contract for any agent that hasn't opted in.
 */
export interface PeerClassifierConfig {
  peer_agent_mode: PeerAgentMode;
  /** chat_ids where peer-agent behaviour is enabled. Empty = DMs only. */
  peer_group_ids: ReadonlyArray<string>;
  /** Peers from the agent's CHARTER `multi_agent.telegram_peers`. */
  peers: ReadonlyArray<PeerInfo>;
  /**
   * ENG-4940 / ENG-4929 §8: channel-agnostic kill switch.
   *   - 'off' (default): no kill.
   *   - 'cross_team_only': drop bot messages whose peer is cross-team
   *     (gate_path !== 'same_team' and not undefined). Same-team peers
   *     and pre-wiring (undefined gate) admit.
   *   - 'all': drop every bot message before peer lookup.
   * The channel mirrors this onto the env var PEER_DISABLED.
   */
  peer_disabled_mode?: 'off' | 'cross_team_only' | 'all';
}

/**
 * Snapshot of our own bot identity. Both fields can be `null` when `getMe`
 * hasn't resolved yet (transient network, fresh start). The classifier
 * treats that as `self_resolution_pending` rather than guessing.
 */
export interface PeerClassifierSelf {
  bot_id: number | null;
  /** Lowercased — match the `resolveBotUsername()` cache convention. */
  bot_username: string | null;
}

export type PeerDropReason =
  | 'mode_off'
  | 'chat_not_allowed'
  | 'unknown_peer'
  | 'not_addressed'
  | 'self_resolution_pending'
  /**
   * ENG-4935: peer is in the registry but no live gate authorises this
   * message. Either a cross-team grant has been revoked / expired since
   * the CHARTER was written, or the org flipped to `consent_required`
   * and the peer's CHARTER entry never carried a grant.
   */
  | 'cross_team_grant_missing'
  /**
   * ENG-4940 / ENG-4929 §8: kill switch is 'all' — every peer surface
   * short-circuited by the team-admin kill switch (PEER_DISABLED env).
   */
  | 'peer_disabled_all'
  /**
   * ENG-4940 / ENG-4929 §8: kill switch is 'cross_team_only' — same-team
   * peers continue admitting, this peer's gate_path is something other
   * than 'same_team' so we drop.
   */
  | 'peer_disabled_cross_team';

export type PeerClassification =
  | { kind: 'human' }
  | { kind: 'self' }
  | { kind: 'drop'; reason: PeerDropReason }
  | { kind: 'peer-ingress'; peer: PeerInfo };

export function classifyPeerMessage(
  msg: PeerClassifierMessage,
  cfg: PeerClassifierConfig,
  self: PeerClassifierSelf,
): PeerClassification {
  // Human path is unchanged — Telegram's User.is_bot is authoritative.
  if (!msg.from?.is_bot) return { kind: 'human' };

  // Defensive: getUpdates shouldn't echo our own outbound messages, but if
  // it ever did we must not loop back into ingress.
  if (self.bot_id !== null && msg.from.id === self.bot_id) {
    return { kind: 'self' };
  }

  // ENG-4940 / ENG-4929 §8: kill switch — 'all' short-circuits every
  // bot-authored message before any other gate. 'cross_team_only' is
  // checked further down after the peer is resolved so we can read its
  // gate_path. 'off' (default) is the no-op path.
  if (cfg.peer_disabled_mode === 'all') {
    return { kind: 'drop', reason: 'peer_disabled_all' };
  }

  // §6.3.1 layer 1 — peer mode opt-in
  if (cfg.peer_agent_mode === 'off') {
    return { kind: 'drop', reason: 'mode_off' };
  }

  // §6.3.1 layer 2 — chat scope
  // Per spec §6.2: empty peer_group_ids → DMs only (peer-agent in a single-
  // agent-single-user DM is a no-op — it's only meaningful in groups, but
  // we still allow it so test rigs can exercise the path without a real
  // group).
  const chatId = String(msg.chat.id);
  if (cfg.peer_group_ids.length === 0) {
    if (msg.chat.type !== 'private') {
      return { kind: 'drop', reason: 'chat_not_allowed' };
    }
  } else if (!cfg.peer_group_ids.includes(chatId)) {
    return { kind: 'drop', reason: 'chat_not_allowed' };
  }

  // §6.3.1 layer 3 — peer registry, keyed on the immutable from.id
  const peer = cfg.peers.find((p) => p.bot_id === msg.from!.id);
  if (!peer) {
    return { kind: 'drop', reason: 'unknown_peer' };
  }

  // ENG-4935 / ENG-4929 §4.0: cross-team gate. `gate_path === null` means
  // the manager looked but couldn't authorise this peer (revoked grant,
  // consent_required toggle with no grant on file). `undefined` keeps the
  // pre-ENG-4935 admit-all contract — older managers haven't been
  // upgraded yet and a wholesale drop here would break in-flight peers.
  if (peer.gate_path === null) {
    return { kind: 'drop', reason: 'cross_team_grant_missing' };
  }

  // ENG-4940 / ENG-4929 §8: kill switch 'cross_team_only' — same-team
  // peers continue admitting, cross-team peers (intra_org_unrestricted
  // OR grant-backed) drop. `undefined` is treated as same-team for
  // backwards compat with managers that haven't shipped ENG-4935.
  if (
    cfg.peer_disabled_mode === 'cross_team_only' &&
    peer.gate_path !== undefined &&
    peer.gate_path !== 'same_team'
  ) {
    return { kind: 'drop', reason: 'peer_disabled_cross_team' };
  }

  // Addressing matrix needs our own identity. If getMe hasn't resolved we
  // can't safely admit anything — fall back to drop with a distinct reason
  // so ops can see the silent-period in telemetry rather than wondering
  // why a peer never got through.
  if (self.bot_id === null || self.bot_username === null) {
    return { kind: 'drop', reason: 'self_resolution_pending' };
  }

  const addressing = classifyAddressing(msg, self.bot_id, self.bot_username);
  if (!addressing.addressed) {
    return { kind: 'drop', reason: 'not_addressed' };
  }

  // ENG-4909 / §5.5 #5: reply-to-our-message is *necessary but not
  // sufficient* by itself. A poisoned upstream (CRM record, scheduled-task
  // prompt) could trigger a peer to relay a "reply to your earlier
  // message" in a fresh DM and slip past the addressing gate. Require
  // either an explicit mention entity for us, OR the chat to be in our
  // CHARTER peer_group_ids list AND our mode to be 'respond' (not
  // 'listen'). In an explicitly-allowlisted group with respond mode the
  // signal is strong enough; in DMs (peer_group_ids empty) reply alone
  // does not pass.
  if (addressing.viaReplyOnly) {
    const inAllowedGroup =
      cfg.peer_group_ids.length > 0 && cfg.peer_group_ids.includes(chatId);
    const respondMode = cfg.peer_agent_mode === 'respond';
    if (!(inAllowedGroup && respondMode)) {
      return { kind: 'drop', reason: 'not_addressed' };
    }
  }
  return { kind: 'peer-ingress', peer };
}

/**
 * §4 addressing matrix. Returns a structured result so the caller can apply
 * the §5.5 #5 reply+mention AND-gate (a reply alone is insufficient unless
 * backed by an explicit mention entity or by the strong combination of
 * 'respond' mode + an allowlisted peer group). A peer message counts as
 * addressed when at least one of the three triggers fires:
 *
 * 1. `bot_command` entity whose `@<username>` suffix is ours (`/cmd@us`).
 *    Bare `/cmd` — no `@` — also counts because the existing /restart
 *    intercept already handles disambiguation when several bots share the
 *    chat.
 * 2. `reply_to_message.from.id === our_bot_id`. The classifier marks this
 *    case as `viaReplyOnly` when no mention/command also fires, so the
 *    AND-gate caller can downgrade it.
 * 3. `mention` entity resolving to our @username. Critically — only
 *    *entity*-bearing mentions count. Plain text "@us look" without a
 *    Telegram-resolved entity is *not* addressed; the spec §2.3 and §4
 *    are explicit that ambiguity here is too expensive.
 */
export interface AddressingResult {
  addressed: boolean;
  /**
   * `true` only when `addressed` is true and the *only* trigger that fired
   * was the reply-to-our-message check. False when a mention or command
   * also fired, or when nothing fired (`addressed: false`).
   */
  viaReplyOnly: boolean;
}

export function classifyAddressing(
  msg: PeerClassifierMessage,
  ourBotId: number,
  ourBotUsername: string,
): AddressingResult {
  let viaMentionOrCommand = false;

  const text = msg.text ?? msg.caption ?? '';
  const entities = msg.entities ?? msg.caption_entities ?? [];
  const usAt = '@' + ourBotUsername;

  for (const entity of entities) {
    const slice = text.slice(entity.offset, entity.offset + entity.length).toLowerCase();
    if (entity.type === 'bot_command') {
      const at = slice.indexOf('@');
      if (at < 0 || slice.slice(at + 1) === ourBotUsername) {
        viaMentionOrCommand = true;
        break;
      }
    } else if (entity.type === 'mention' && slice === usAt) {
      viaMentionOrCommand = true;
      break;
    }
  }

  const viaReply = msg.reply_to_message?.from?.id === ourBotId;

  if (viaMentionOrCommand) return { addressed: true, viaReplyOnly: false };
  if (viaReply) return { addressed: true, viaReplyOnly: true };
  return { addressed: false, viaReplyOnly: false };
}

/** @deprecated kept for compat; prefer `classifyAddressing` for the AND-gate. */
export function isAddressedToUs(
  msg: PeerClassifierMessage,
  ourBotId: number,
  ourBotUsername: string,
): boolean {
  return classifyAddressing(msg, ourBotId, ourBotUsername).addressed;
}

// Mirrors the kebab-case constraint enforced on `code_name` everywhere else
// (CHARTER frontmatter JSON Schema, package code_name fields, etc.). Keep the
// shape identical so the env path can't smuggle invalid identifiers past a
// charter that would have rejected them.
const CODE_NAME_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Parse the `TELEGRAM_PEERS` env var (a JSON array of
 * `{ code_name, bot_id, agent_id }` triples). Invalid entries are skipped
 * silently rather than failing startup — the classifier just won't
 * recognise that peer, which matches the silent-drop behaviour we'd see
 * from a missing CHARTER entry anyway.
 *
 * ENG-4935: if `gateRaw` (the `TELEGRAM_PEERS_GATE` env var) is supplied,
 * each peer's `gate_path` is folded in from a `{ bot_id: gate_path }`
 * JSON map. A peer with no entry in the gate map gets `gate_path:
 * undefined`, which the classifier treats as the pre-ENG-4935 admit
 * path (preserves the contract for managers that haven't been upgraded).
 */
export function parsePeersEnv(
  raw: string | undefined,
  gateRaw?: string | undefined,
): PeerInfo[] {
  if (!raw || raw.trim().length === 0) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  // ENG-4935: peer-id → gate_path map.
  //
  // CodeRabbit on PR #868: when TELEGRAM_PEERS_GATE is *present but
  // malformed*, fail closed (every peer's gate_path = null → classifier
  // drops with `cross_team_grant_missing`). The pre-fix behaviour
  // silently fell back to admit-all, which re-opens the revoked /
  // expired / missing-grant cases this PR is meant to block. An absent
  // env (gateRaw undefined / empty) still falls through to the
  // documented pre-ENG-4935 admit path for managers that haven't been
  // upgraded.
  const gateMap = new Map<string, PeerGatePath>();
  let gateConfigInvalid = false;
  // ENG-7786: whether a gate env was supplied at all. When present, a
  // registered peer MISSING from the map fails closed (below); when absent,
  // gate_path stays undefined and the classifier admits (pre-wiring compat).
  const gatePresent = !!(gateRaw && gateRaw.trim().length > 0);
  if (gatePresent) {
    try {
      const gateParsed = JSON.parse(gateRaw);
      if (!gateParsed || typeof gateParsed !== 'object' || Array.isArray(gateParsed)) {
        gateConfigInvalid = true;
      } else {
        for (const [k, v] of Object.entries(gateParsed as Record<string, unknown>)) {
          if (v === null) {
            gateMap.set(k, null);
          } else if (typeof v === 'string') {
            // CodeRabbit on PR #868: `"grant:"` (empty grant id) used to
            // slip through the allowlist because startsWith ignores the
            // suffix. Require a non-empty id after the prefix so a
            // malformed entry fails closed via the gateConfigInvalid path.
            const isGrantPath =
              v.startsWith('grant:') && v.slice('grant:'.length).trim().length > 0;
            if (
              v === 'same_team' ||
              v === 'intra_org_unrestricted' ||
              isGrantPath
            ) {
              gateMap.set(k, v as PeerGatePath);
            } else {
              // Unknown gate path value — treat the whole env as invalid
              // rather than dropping just this entry, so a typo in one
              // peer's gate doesn't admit other peers via the absent-key
              // fallback.
              gateConfigInvalid = true;
              break;
            }
          } else {
            gateConfigInvalid = true;
            break;
          }
        }
      }
    } catch {
      gateConfigInvalid = true;
    }
    if (gateConfigInvalid) {
      // Use stderr (matches the rest of the MCP boot logging) so the
      // operator notices the misconfiguration without having to grep
      // through Telegram traffic logs to find the silent drops.
      console.error(
        '[telegram-peer-classifier] TELEGRAM_PEERS_GATE is present but malformed; failing closed (every peer drops as cross_team_grant_missing)',
      );
    }
  }

  const out: PeerInfo[] = [];
  for (const entry of parsed) {
    if (
      entry &&
      typeof entry === 'object' &&
      typeof (entry as { code_name?: unknown }).code_name === 'string' &&
      CODE_NAME_RE.test((entry as { code_name: string }).code_name) &&
      typeof (entry as { agent_id?: unknown }).agent_id === 'string' &&
      typeof (entry as { bot_id?: unknown }).bot_id === 'number' &&
      Number.isInteger((entry as { bot_id: number }).bot_id) &&
      (entry as { bot_id: number }).bot_id > 0
    ) {
      const e = entry as { code_name: string; agent_id: string; bot_id: number };
      const peer: PeerInfo = { code_name: e.code_name, bot_id: e.bot_id, agent_id: e.agent_id };
      if (gateConfigInvalid) {
        // Fail-closed sentinel — drop every peer with cross_team_grant_missing.
        peer.gate_path = null;
      } else {
        const key = String(e.bot_id);
        if (gateMap.has(key)) {
          peer.gate_path = gateMap.get(key) ?? null;
        } else if (gatePresent) {
          // ENG-7786: a present, valid gate map that OMITS this registered
          // peer fails closed - drop as cross_team_grant_missing rather than
          // admitting via the pre-wiring `undefined` fallback. `undefined` is
          // reserved for "no gate env at all" (older manager, backwards compat).
          peer.gate_path = null;
        }
      }
      out.push(peer);
    }
  }
  return out;
}

export function parsePeerGroupIdsEnv(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

export function parsePeerAgentModeEnv(raw: string | undefined): PeerAgentMode {
  if (raw === 'listen' || raw === 'respond') return raw;
  // Any other value (including unset / typo) defaults to 'off' — the safe
  // default that preserves single-agent behaviour.
  return 'off';
}
